import { objectToFormData } from '@octetstream/object-to-form-data'
import type { AxiosError, AxiosResponse } from 'axios'
import useApi from '@/composables/useApi'
import { cleanEmptyFieldsPayload } from '@/utils/generals'

class ApiService {
  private readonly _endpoint: string
  private readonly _group?: string
  private readonly _isPublic?: boolean

  /**
   * Initializes a new instance of the class with the specified endpoint.
   *
   * @param {string} endpoint - The endpoint to be set for the instance.
   * @param group
   * @param isPublic
   * @return {void}
   */
  public constructor(endpoint: string, group?: string, isPublic?: boolean) {
    this._endpoint = endpoint
    this._group = group
    this._isPublic = isPublic
  }

  /**
   * Determines if the provided data contains any File or Blob objects.
   *
   * @param {any} data - The data object to inspect for File or Blob instances.
   * @return {boolean} - Returns true if any value in the data is a File or Blob, otherwise false.
   */
  protected hasFiles(data: any): boolean {
    if (!data || typeof data !== 'object')
      return false

    // Verifica se algum valor do payload é um tipo File ou Blob
    return Object.values(data).some(value => value instanceof File || value instanceof Blob)
  }

  /**
   * Preprocesses the provided data object. If the data contains files, it is
   * converted to FormData. Otherwise, empty fields within the data are cleaned.
   *
   * @param {object} data - The data object to be processed. Defaults to an empty object.
   * @param formData
   * @return {any} - Processed data, either in FormData format or cleaned.
   */
  protected preprocessData(data = {}, formData?: boolean): any {
    data = cleanEmptyFieldsPayload(data)

    if (this.hasFiles(data) || formData) {
      // Se há arquivos, usa FormData
      return objectToFormData(data)
    }

    return data
  }

  /**
   * Constructs a full URL by appending a query string to the base URL.
   *
   * @param {object} [query] - An optional object representing the query parameters.
   * @param id
   * @return {string} The full URL with the query string appended.
   */
  protected getURL(query: any, id?: string): string {
    let baseUrl = ''

    if (this._group)
      baseUrl += `/${this._group}`

    baseUrl += `/${this._endpoint}`

    if (id)
      baseUrl += `/${id}`

    query = cleanEmptyFieldsPayload(query)
    query = objectToFormData(query)

    // Converte o objeto de consulta em uma string de consulta
    const queryString = new URLSearchParams(query as any).toString()

    // Remove the trailing slash if it exists
    const sanitizedUrl = baseUrl.replace(/\/$/, '')

    return queryString ? `${sanitizedUrl}?${queryString}` : sanitizedUrl
  }

  /**
   * Sends a POST or PUT request to the specified endpoint.
   *
   * @param {string} httpVerb - The HTTP verb to be used for the request (either 'POST' or 'PUT').
   * @param {object|string} [data] - The data to be sent with the request. Can be an object or a string.
   * @param {string} [id] - An optional identifier to be appended to the endpoint URL.
   * @param formData
   * @return {Promise<AxiosResponse<TRequest>>} - Returns a promise that resolves with the response data or undefined if an error occurs.
   */
  protected async postOrPutRequest<TRequest>(
    httpVerb: string,
    data?: object | string,
    id?: string,
    formData?: boolean,
  ): Promise<AxiosResponse<TRequest>> {
    // Pre-processa dados
    const payload = this.preprocessData(data, formData)
    const url = this.getURL(null, id)

    if (payload instanceof FormData && httpVerb === 'PUT') {
      payload.append('_method', 'PUT')
      httpVerb = 'POST'
    }

    return await useApi.request<TRequest>({
      url,
      method: httpVerb,
      data: payload,
      headers: payload instanceof FormData ? { 'Content-Type': 'multipart/form-data', 'public': this._isPublic } : { public: this._isPublic },
    })
  }

  /**
   * Send an HTTP GET or DELETE request.
   *
   * @param {string} httpVerb - The HTTP verb to use ('GET' or 'DELETE').
   * @param {any} [query] - Optional query parameters.
   * @param {string} [id] - Optional ID to append to the endpoint URL.
   * @return {AxiosResponse<TRequest>} The response data of type TRequest or undefined if an error occurs.
   */
  protected async getOrDeleteRequest<TRequest>(
    httpVerb: string,
    query?: any,
    id?: string,
  ): Promise<AxiosResponse<TRequest>> {
    const url = this.getURL(query, id)

    return await useApi.request<TRequest>({
      url,
      method: httpVerb,
      headers: {
        'Content-Type': 'application/json',
        'public': this._isPublic,
      },
    })
  }

  /**
   * Sends a POST request to create a new resource with the provided data.
   * If an ID is also provided, it might update an existing resource.
   *
   * @param {object} data - The data to be sent with the POST request.
   * @param {string} [id] - An optional ID indicating the resource to update.
   * @param formData
   * @return {Promise<TRequest>} - A promise that resolves to the created resource or undefined.
   */
  public async create<TRequest>(data: object, id?: string, formData?: boolean): Promise<AxiosResponse<TRequest, any>> {
    return await this.postOrPutRequest<TRequest>('POST', data, id, formData)
  }

  /**
   * Sends a POST request with the specified data and optional ID.
   *
   * @param {object} data - The data to be sent in the POST request.
   * @param {string} [id] - An optional ID to be included in the request.
   * @param formData
   * @return {Promise<TRequest>} - A promise resolving to the response of the POST request, or undefined if the request fails.
   */
  public async post<TRequest>(data: object, id?: string, formData?: boolean): Promise<AxiosResponse<TRequest, any>> {
    return await this.postOrPutRequest<TRequest>('POST', data, id, formData)
  }

  public async update<TRequest>(data: object, id?: string, formData?: boolean): Promise<AxiosResponse<TRequest, any>> {
    return await this.postOrPutRequest<TRequest>('PUT', data, id, formData)
  }

  /**
   * Updates a resource identified by the given ID with the provided data.
   *
   * @param {object} data - The data to update the resource with.
   * @param {string} id - The unique identifier of the resource to be updated.
   * @return {Promise<TRequest>} A promise that resolves to the updated resource or undefined if not found.
   */
  public async patch<TRequest>(data: Record<string, any>, id?: string) {
    return await this.postOrPutRequest<TRequest>('PATCH', data, id)
  }

  /**
   * Fetches all data based on the given query and URL.
   *
   * @param {any} [query] - Optional query parameters to filter the results.
   * @param {string} [url] - Optional URL to override the default endpoint.
   * @return {Promise<TRequest>} - A promise that resolves with the fetched data or undefined if not found.
   */
  public async fetchAll<TRequest>(query?: any, url?: string): Promise<AxiosResponse<TRequest, any>> {
    return await this.getOrDeleteRequest<TRequest>('GET', query, url)
  }

  /**
   * Fetches data based on the provided ID.
   *
   * @param {string} id - The identifier for the data to be fetched.
   * @return {Promise<TRequest>} A promise that resolves to the fetched data of type TRequest, or undefined if not found.
   */
  public async fetch<TRequest>(id: string): Promise<AxiosResponse<TRequest, any>> {
    return await this.getOrDeleteRequest<TRequest>('GET', undefined, id)
  }

  /**
   * Deletes a resource identified by the given ID.
   *
   * @param {string} id - The unique identifier of the resource to be deleted.
   * @return {Promise<TRequest>} - A promise that resolves to the deleted resource or undefined.
   */
  public async destroy<TRequest>(id: string): Promise<AxiosResponse<TRequest, any>> {
    return await this.getOrDeleteRequest<TRequest>('DELETE', undefined, id)
  }

  /**
   * Searches for a record based on the specified field and value, with optional pagination, relation, and ordering.
   *
   * @param {string} field - The field of the record to search by.
   * @param {string} value - The value of the field to search for.
   * @param {number} page - The page number for pagination.
   * @param {string} [relation] - An optional parameter to specify related records.
   * @param {object} [orderBy] - An optional parameter to specify sorting order.
   * @param params
   * @param {string} orderBy.key - The field to order by.
   * @param {string} orderBy.order - The order direction (e.g., 'asc' or 'desc').
   *
   * @return {Promise<TRequest>} A promise that resolves to the search result or undefined if not found.
   */
  async search<TRequest>(field: string, value: string, page: number, relation?: string, orderBy?: any, params?: any): Promise<AxiosResponse<TRequest, any>> {
    const valueEncode = encodeURIComponent(value)

    return await this.getOrDeleteRequest<TRequest>('GET', {
      page,
      sort_by: orderBy.key,
      sort_order: orderBy.order,
      ...params,
    }, `pesquisarpor/${field}/${valueEncode}/${relation}`)
  }

  /**
   * Handles errors encountered during an API call. If in a development environment,
   * logs the error to the console and rethrows it.
   * @param {AxiosError} error - The error object received from the Axios call.
   * @return {void}
   */
  protected handleError(error: AxiosError) {
    if (import.meta.env.DEV) {
      console.error('APIService Error:', error)
      throw error
    }
  }
}

export default ApiService
